import { useState } from 'owa-react';
import { FluentMenu } from 'owa-fluent-v9-shims';
import calculateColumnWidths, {
    updateSortedColumnWidthCache,
} from '../utils/calculateColumnWidths';
import { observer } from 'owa-mobx-react';
import { type FolderForestNodeType } from 'owa-favorites-types';
import loc, { format } from 'owa-localize';
import { lazySelectSort } from 'owa-mail-filter-actions';
import { getSelectedNode } from 'owa-mail-folder-forest-store';
import { getAnchorForContextMenu } from 'owa-positioning';
import { useMessageListTextStyle } from 'owa-mail-listitem-styles/lib/utils/useMessageListTextStyle';
import { useResizeObserver } from 'owa-react-hooks/lib/useResizeObserver';
import {
    ResizeHandle,
    ResizeHandleScenario,
    lazyUpdateScenarioInResizeMode,
} from 'owa-resize-handle';
import type SortDirection from 'owa-service/lib/contract/SortDirection';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';
import { getDensityModeCssClass } from 'owa-fabric-theme';
import React from 'react';
import {
    getSortByForTable,
    getUserCategoryName,
    getViewFilterForTable,
    isDumpsterTable,
    getStore as getListViewStore,
    type SortColumn,
    type TableQueryType,
    getIsSearchTableShown,
    isCopilotResultsTable,
    isPriorityView,
    isNeedsReplyView,
    type MailFolderTableQuery,
} from 'owa-mail-list-store';
import {
    columnHeaderAriaLabel,
    fromColumnLabel,
    modifiedColumnLabel,
    receivedColumnLabel,
    returnTimeColumnLabel,
    sentColumnLabel,
    sortAscendingLabel,
    sortDescendingLabel,
    subjectColumnLabel,
    toColumnLabel,
    resizeButtonLabel,
    categoryColumnLabel,
    importanceColumnLabel,
    priorityColumnLabel,
    flagStatusColumnLabel,
    flagDueDateColumnLabel,
    sizeColumnLabel,
} from './MailListColumnHeaders.locstring.json';
import type { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { DirectionalHint } from '@fluentui/react/lib/ContextualMenu';
import {
    getStore as getMailListLayoutStore,
    onColumnHeaderWidthsUpdated,
    onFirstColumnHandleChanged,
    onSecondColumnHandleChanged,
    onSortedContentColumnHandleChanged,
    type MailListColumnHeaderType,
    getColumnMinWidth,
    getSortedContentColumnHeaderType,
} from 'owa-mail-layout';
import { getDisplayDensityMenuItem } from 'owa-display-density-option';
import type { IPoint } from '@fluentui/react/lib/Utilities';
import { wrapForcedLayout } from 'owa-performance';
import {
    columnHeadersContainer,
    columnHeader,
    columnHeaderTypeRamp,
    resizeHandle,
    columnHeaderRoomy,
    columnHeaderRoomyTypeRampUpdates,
    columnHeaderCozy,
    columnHeaderCompact,
    sortIconRoomy,
    sortIconCompactOrCozy,
    columnHeadersContainerDenseless,
    bleedthroughColumnHeader,
    columnHeaderContainer,
    sortingEnabled,
    receivedColumnContainer,
    sortedColumnHeaderTitle,
    arrowCircleIcon,
    columnHeaderTypeRampColorUpdate,
    senderColumnButtonMarginFull,
    senderColumnButtonMarginMediumCompact,
} from './MailListColumnHeaders.scss';
import classnames from 'owa-classnames';
import { useIsTypeRampUpdatesEnabled } from 'owa-mail-list-item-shared/lib/utils/useIsTypeRampUpdatesEnabled';
import {
    bundleIcon,
    ChevronDownRegular,
    ChevronUpRegular,
    ArrowCircleUp16Filled,
    ArrowCircleUp16Regular,
    Important16Filled,
    Flag16Filled,
} from '@fluentui/react-icons';

const ArrowCircleUp = bundleIcon(ArrowCircleUp16Filled, ArrowCircleUp16Regular);
const ArrowCircleUpIcon = <ArrowCircleUp className={arrowCircleIcon} />;

export interface MailListColumnHeadersProps {
    tableViewId: string;
    folderId: string;
}
const MailListColumnHeaders: React.FunctionComponent<MailListColumnHeadersProps> = observer(
    function MailListColumnHeaders(props: MailListColumnHeadersProps) {
        const { folderId, tableViewId } = props;
        const isTypeRampUpdatesEnabled = useIsTypeRampUpdatesEnabled();
        const densityModeCssClass = getDensityModeCssClass(
            isTypeRampUpdatesEnabled ? columnHeaderRoomyTypeRampUpdates : columnHeaderRoomy,
            columnHeaderCozy,
            columnHeaderCompact
        );

        const {
            senderColumnWidth,
            subjectColumnWidth,
            receivedColumnWidth,
            sortedContentColumnWidth,
        } = getMailListLayoutStore();
        const isRPDenselessEnabled = useIsTypeRampUpdatesEnabled();
        /* eslint-disable-next-line owa-custom-rules/prefer-react-state-without-arrays-or-objects -- (https://aka.ms/OWALintWiki)
         * Please remove the array or object from React.useState() or leave a justification in case is not possible to do so.
         *	> It is preferable not to use arrays or objects as react state, destruct the state into primitive data types or use satchel instead. */
        const [currentSortColumn, setCurrentSortColumn] = useState<SortColumn | null>(null);
        const [currentSortDirection, setCurrentSortDirection] = useState<SortDirection | null>(
            /* eslint-disable-next-line owa-custom-rules/prefer-react-state-without-arrays-or-objects -- (https://aka.ms/OWALintWiki)
             * Please remove the array or object from React.useState() or leave a justification in case is not possible to do so.
             *	> It is preferable not to use arrays or objects as react state, destruct the state into primitive data types or use satchel instead. */
            null
        );

        const containerRef = React.useRef<HTMLDivElement>(null);
        const sortedContentColumnHeaderType = React.useMemo(() => {
            const tableView = getListViewStore().tableViews.get(tableViewId);
            if (!tableView) {
                return null;
            }
            return getSortedContentColumnHeaderType(tableView);
        }, [tableViewId]);

        /**
         * Using this tracking ref is a workaround as useResizeObserver isn't working
         * how I'd expect. Instead of only firing when the element ref's size changes,
         * it appears to be over-firing. I'm using this tracking ref to determine if
         * the callback needs to actually run.
         */
        const prevContainerWidth = React.useRef(null);

        // Recalculate column widths when sort column type changes
        React.useEffect(() => {
            const containerWidth = prevContainerWidth.current;
            if (!containerWidth) {
                return;
            }

            // Reset to force recalculation
            prevContainerWidth.current = null;

            const columnWidths = calculateColumnWidths(
                false /* isInitialLayout */,
                containerWidth,
                prevContainerWidth,
                sortedContentColumnHeaderType
            );
            if (
                !columnWidths.senderColumnWidth ||
                !columnWidths.subjectColumnWidth ||
                !columnWidths.receivedColumnWidth
            ) {
                return;
            }
            onColumnHeaderWidthsUpdated(
                columnWidths.senderColumnWidth,
                columnWidths.subjectColumnWidth,
                columnWidths.receivedColumnWidth,
                columnWidths.sortedContentColumnWidth
            );
        }, [sortedContentColumnHeaderType]);

        React.useEffect(() => {
            let containerWidth: number | undefined;
            wrapForcedLayout('MailListColumnHeaders_containerWidth', () => {
                /* eslint-disable-next-line no-restricted-properties -- (https://aka.ms/OWALintWiki)
                 * This is baseline exception, if you edit this file you need to fix this exception.
                 * Baseline, please provide a proper justification if touching this code
                 *	> 'offsetWidth' is restricted from being used. This property can cause performance problems by causing re-layouts. Please avoid if possible; if not, move to a requestAnimationFrame callback, and perform all DOM reads before performing any writes. */
                containerWidth = containerRef?.current?.offsetWidth;
            });
            if (!containerWidth) {
                return;
            }

            const columnWidths = calculateColumnWidths(
                true /* isInitialLayout */,
                containerWidth,
                prevContainerWidth,
                sortedContentColumnHeaderType
            );
            if (
                !columnWidths.senderColumnWidth ||
                !columnWidths.subjectColumnWidth ||
                !columnWidths.receivedColumnWidth
            ) {
                return;
            }

            onColumnHeaderWidthsUpdated(
                columnWidths.senderColumnWidth,
                columnWidths.subjectColumnWidth,
                columnWidths.receivedColumnWidth,
                sortedContentColumnHeaderType && columnWidths.sortedContentColumnWidth === 0
                    ? getColumnMinWidth(sortedContentColumnHeaderType)
                    : columnWidths.sortedContentColumnWidth
            );
        }, [sortedContentColumnHeaderType]);

        // Resize columns if container size changes.
        useResizeObserver('MailListColumnHeaders', containerRef, rect => {
            const containerWidth = rect.width;
            // Don't update column widths if container isn't visible on screen.
            if (!containerWidth) {
                return;
            }

            const columnWidths = calculateColumnWidths(
                false /* isInitialLayout */,
                containerWidth,
                prevContainerWidth,
                sortedContentColumnHeaderType
            );
            if (
                !columnWidths.senderColumnWidth ||
                !columnWidths.subjectColumnWidth ||
                !columnWidths.receivedColumnWidth
            ) {
                return;
            }
            onColumnHeaderWidthsUpdated(
                columnWidths.senderColumnWidth,
                columnWidths.subjectColumnWidth,
                columnWidths.receivedColumnWidth,
                columnWidths.sortedContentColumnWidth
            );
        });

        const onColumnResize = React.useCallback(
            (
                firstElementWidth: number,
                secondElementWidth: number,
                headerType: MailListColumnHeaderType
            ) => {
                if (headerType === 0) {
                    onFirstColumnHandleChanged(firstElementWidth, secondElementWidth);
                } else if (headerType === 1) {
                    if (sortedContentColumnHeaderType && receivedColumnWidth !== null) {
                        // received column width stays the same, secondElementWidth is for sorted content column
                        onSecondColumnHandleChanged(
                            firstElementWidth,
                            receivedColumnWidth,
                            secondElementWidth
                        );
                        // Persist the manually-dragged sorted column width in the cache
                        // so it's respected by subsequent window resizes.
                        updateSortedColumnWidthCache(
                            sortedContentColumnHeaderType,
                            secondElementWidth
                        );
                    } else {
                        // No sorted content column available thus no width
                        onSecondColumnHandleChanged(
                            firstElementWidth,
                            secondElementWidth,
                            sortedContentColumnWidth ?? 0
                        );
                    }
                } else {
                    // Sorted content column resize case
                    onSortedContentColumnHandleChanged(firstElementWidth, secondElementWidth);
                    // Persist the manually-dragged sorted column width in the cache
                    // so it's respected by subsequent window resizes.
                    if (sortedContentColumnHeaderType) {
                        updateSortedColumnWidthCache(
                            sortedContentColumnHeaderType,
                            firstElementWidth
                        );
                    }
                }
            },
            [sortedContentColumnHeaderType, receivedColumnWidth, sortedContentColumnWidth]
        );

        const isSortingDisabled = React.useMemo(() => {
            const tableView = getListViewStore().tableViews.get(tableViewId);
            if (!tableView) {
                // Disable sorting if tableView is undefined
                return true;
            }

            const categoryName = getUserCategoryName(tableView);
            const isCategoryFolder = categoryName !== null;
            const isDumpster = isDumpsterTable(tableView.tableQuery);
            const isGroupTable = tableView.tableQuery.type === 2 || tableView.tableQuery.type === 3;
            const isSearchTable = tableView.tableQuery.type === 1;
            const isPriorityViewValue = isPriorityView(
                tableView.tableQuery as MailFolderTableQuery
            );
            const isNeedsReplyViewValue = isNeedsReplyView(
                tableView.tableQuery as MailFolderTableQuery
            );

            // Determine if current table is a new persona favorite.
            const isPersonaFavoriteFolder = getSelectedNode().type === 1;
            const isInitialPersonaFavoriteFolder =
                isPersonaFavoriteFolder && getViewFilterForTable(tableView) == null;

            return (
                isCategoryFolder ||
                isDumpster ||
                isGroupTable ||
                isSearchTable ||
                isCopilotResultsTable(tableView.tableQuery) ||
                isInitialPersonaFavoriteFolder ||
                isPriorityViewValue ||
                isNeedsReplyViewValue
            );
        }, [tableViewId]);

        // Reset sort column/direction when folder or folder sort changes.
        React.useEffect(() => {
            // If sorting is disabled, then we don't need to get sort info.
            if (isSortingDisabled) {
                return;
            }

            const tableView = getListViewStore().tableViews.get(tableViewId);
            if (!tableView) {
                return;
            }

            const sortBy = getSortByForTable(tableView);
            /* eslint-disable-next-line owa-custom-rules/forbid-react-set-state-and-mutators-inside-specific-hooks -- (https://aka.ms/OWALintWiki)
             * BASELINE. DO NOT COPY AND PASTE!, replace this line with a justification or fix the error.
             *	> React setState function 'setCurrentSortColumn' cannot be called inside 'useEffect' to avoid unnecessary re-renders */
            setCurrentSortColumn(sortBy.sortColumn);
            /* eslint-disable-next-line owa-custom-rules/forbid-react-set-state-and-mutators-inside-specific-hooks -- (https://aka.ms/OWALintWiki)
             * BASELINE. DO NOT COPY AND PASTE!, replace this line with a justification or fix the error.
             *	> React setState function 'setCurrentSortDirection' cannot be called inside 'useEffect' to avoid unnecessary re-renders */
            setCurrentSortDirection(sortBy.sortDirection);
        }, [folderId, isSortingDisabled, tableViewId]);

        const onColumnClicked = React.useCallback(
            (sortColumn: SortColumn) => {
                /**
                 * The default sort for the "Received" column is "Descending" (most
                 * recent on top) where as the default sort for the other columns is
                 * "Ascending" (albabetical order).
                 */
                let sortDirection: SortDirection = sortColumn === 1 ? 'Descending' : 'Ascending';

                /**
                 * If sort column is the same as current sort, then invert the sort
                 * direction. Otherwise, default to "Descending" sort.
                 */
                if (sortColumn === currentSortColumn) {
                    switch (currentSortDirection) {
                        case 'Descending':
                            sortDirection = 'Ascending';
                            break;
                        case 'Ascending':
                        default:
                            sortDirection = 'Descending';
                            break;
                    }
                }

                setCurrentSortColumn(sortColumn);
                setCurrentSortDirection(sortDirection);

                lazySelectSort.importAndExecute(
                    sortColumn,
                    sortDirection,
                    tableViewId,
                    'ListViewColumnHeader'
                );
            },
            [currentSortColumn, currentSortDirection, tableViewId]
        );

        const onSortByContextualMenu = React.useCallback(
            (sortColumn: SortColumn, sortDirection: SortDirection) => {
                setCurrentSortColumn(sortColumn);
                setCurrentSortDirection(sortDirection);
            },
            []
        );

        /**
         * Don't render column headers if sort info hasn't been initialized and sorting
         * is enabled. If sorting is disabled, then we don't need sort info and we
         * can render.
         */
        if (!currentSortColumn && !isSortingDisabled) {
            return null;
        }

        return (
            <div
                className={classnames(
                    isRPDenselessEnabled && columnHeadersContainerDenseless,
                    !isSortingDisabled && sortingEnabled,
                    columnHeadersContainer,
                    densityModeCssClass
                )}
                ref={containerRef}
            >
                <MailListColumnHeader
                    currentSortColumn={currentSortColumn}
                    currentSortDirection={currentSortDirection}
                    folderId={folderId}
                    headerType={0}
                    isSortingDisabled={isSortingDisabled}
                    onColumnClicked={onColumnClicked}
                    onColumnResize={onColumnResize}
                    onSortByContextualMenu={onSortByContextualMenu}
                    tableViewId={tableViewId}
                    width={senderColumnWidth ?? 0}
                />
                <MailListColumnHeader
                    currentSortColumn={currentSortColumn}
                    currentSortDirection={currentSortDirection}
                    folderId={folderId}
                    headerType={1}
                    isSortingDisabled={isSortingDisabled}
                    onColumnClicked={onColumnClicked}
                    onColumnResize={onColumnResize}
                    onSortByContextualMenu={onSortByContextualMenu}
                    tableViewId={tableViewId}
                    width={subjectColumnWidth ?? 0}
                />
                {sortedContentColumnHeaderType && (
                    <MailListColumnHeader
                        currentSortColumn={currentSortColumn}
                        currentSortDirection={currentSortDirection}
                        folderId={folderId}
                        headerType={sortedContentColumnHeaderType}
                        isSortingDisabled={isSortingDisabled}
                        onColumnClicked={onColumnClicked}
                        onColumnResize={onColumnResize}
                        onSortByContextualMenu={onSortByContextualMenu}
                        tableViewId={tableViewId}
                        width={sortedContentColumnWidth ?? 0}
                    />
                )}
                <MailListColumnHeader
                    currentSortColumn={currentSortColumn}
                    currentSortDirection={currentSortDirection}
                    folderId={folderId}
                    headerType={2}
                    isSortingDisabled={isSortingDisabled}
                    onColumnClicked={onColumnClicked}
                    onColumnResize={onColumnResize}
                    onSortByContextualMenu={onSortByContextualMenu}
                    tableViewId={tableViewId}
                    width={receivedColumnWidth ?? 0}
                />
            </div>
        );
    },
    'MailListColumnHeaders'
);

export interface MailListColumnHeaderProps {
    currentSortColumn: SortColumn | null;
    currentSortDirection: SortDirection | null;
    folderId: string;
    headerType: MailListColumnHeaderType;
    isSortingDisabled: boolean;
    onColumnClicked: (sortColumn: SortColumn) => void;
    onColumnResize: (
        firstElementWidth: number,
        secondElementWidth: number,
        headerType: MailListColumnHeaderType
    ) => void;
    onSortByContextualMenu: (sortColumn: SortColumn, sortDirection: SortDirection) => void;
    tableViewId: string;
    width: number;
}
const MailListColumnHeader = observer(function MailListColumnHeader(
    props: MailListColumnHeaderProps
) {
    const {
        currentSortColumn,
        currentSortDirection,
        folderId,
        headerType,
        isSortingDisabled,
        onColumnClicked,
        onColumnResize,
        onSortByContextualMenu,
        tableViewId,
        width,
    } = props;

    const isTypeRampEnabled = useIsTypeRampUpdatesEnabled();

    // Context menu state
    const [isContextMenuVisible, setIsContextMenuVisible] = useState(false);
    /* eslint-disable-next-line owa-custom-rules/prefer-react-state-without-arrays-or-objects -- (https://aka.ms/OWALintWiki)
     * Please remove the array or object from React.useState() or leave a justification in case is not possible to do so.
     *	> It is preferable not to use arrays or objects as react state, destruct the state into primitive data types or use satchel instead. */
    const [anchorPoint, setAnchorPoint] = useState<IPoint | null>(null);

    const isRPDenselessEnabled = useIsTypeRampUpdatesEnabled();

    // For received column, don't set explicit width - it will flex to fill remaining space
    const isReceivedColumn = headerType === 2;
    const buttonStyles = useComputedValue(() => {
        let computedWidth: number | undefined = width;
        if (isReceivedColumn) {
            computedWidth = undefined;
        }

        return {
            width: computedWidth,
            boxSizing: 'border-box' as const,
            cursor: isSortingDisabled ? 'default' : 'pointer',
        };
    }, [isSortingDisabled, isReceivedColumn, width]);

    const onResized = React.useCallback(
        (firstElementWidth: number, secondElementWidth: number) => {
            onColumnResize(firstElementWidth, secondElementWidth, headerType);
        },
        [onColumnResize, headerType]
    );

    const folderName = folderId ? folderIdToName(folderId, true /* shouldSearchShared */) : null;

    const onClick = React.useCallback(
        (evt: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            evt.stopPropagation();

            onColumnClicked(getSortColumnByMailListColumnHeaderType(headerType, folderName ?? ''));
        },
        [headerType, folderName, onColumnClicked]
    );

    const onContextMenu = React.useCallback(
        (evt: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            evt.preventDefault();
            evt.stopPropagation();
            setAnchorPoint(getAnchorForContextMenu(evt));
            setIsContextMenuVisible(true);
        },
        []
    );

    const onDismiss = React.useCallback(
        (evt?: Event | React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>) => {
            if (evt) {
                evt.stopPropagation();
            }

            setIsContextMenuVisible(false);
        },
        []
    );

    const iconClass = getDensityModeCssClass(
        sortIconRoomy,
        sortIconCompactOrCozy,
        sortIconCompactOrCozy
    );

    const senderColumnButtonMarginClass = getDensityModeCssClass(
        senderColumnButtonMarginFull,
        senderColumnButtonMarginMediumCompact,
        senderColumnButtonMarginMediumCompact
    );

    // Get sort column associated with header type.
    const sortColumnForHeader = React.useMemo(
        () => getSortColumnByMailListColumnHeaderType(headerType, folderName ?? ''),
        [headerType, folderName]
    );

    const icon = React.useMemo(() => {
        /**
         * Don't render icon if table isn't sorted by this column or if sorting
         * is disabled.
         */
        if (currentSortColumn !== sortColumnForHeader || isSortingDisabled) {
            return null;
        }

        // Return icon based on sort direction.
        const isSortDirectionAscending = currentSortDirection === 'Ascending';
        return isSortDirectionAscending ? (
            <ChevronUpRegular className={iconClass} />
        ) : (
            <ChevronDownRegular className={iconClass} />
        );
    }, [
        currentSortColumn,
        currentSortDirection,
        isSortingDisabled,
        iconClass,
        sortColumnForHeader,
    ]);

    const columnLabel = getColumnLabel(folderId, headerType);

    const resizeHandleScenario = React.useMemo(() => {
        // Sender column always refers to the first resize handle, so return
        // scenario 1.
        if (headerType === 0) {
            return ResizeHandleScenario.MailListColumnHeader1;
        }

        if (headerType === 1) {
            return ResizeHandleScenario.MailListColumnHeader2;
        }

        if (headerType === 2) {
            return ResizeHandleScenario.MailListColumnHeader2;
        }

        if (
            headerType === 3 ||
            headerType === 4 ||
            headerType === 5 ||
            headerType === 6 ||
            headerType === 7 ||
            headerType === 8
        ) {
            return ResizeHandleScenario.MailListColumnHeaderSortedContent;
        }

        return null;
    }, [headerType]);

    const shouldApplySortedContentColumnStyles =
        resizeHandleScenario === ResizeHandleScenario.MailListColumnHeaderSortedContent;

    const resizeHandleComponent = React.useMemo(() => {
        // There's no resize handle after the last column.
        if (headerType === 2 || resizeHandleScenario === null) {
            return null;
        }

        // Otherwise, we return an actual ResizeHandle component that can be resized.
        return (
            <ResizeHandle
                onResized={onResized}
                className={classnames(resizeHandle)}
                scenario={resizeHandleScenario}
            />
        );
    }, [headerType, onResized, resizeHandleScenario]);

    const sortedContentHeaderIcon = React.useMemo(() => {
        if (headerType === 4) {
            return <Important16Filled />;
        } else if (headerType === 5) {
            return ArrowCircleUpIcon;
        } else if (headerType === 6) {
            return <Flag16Filled />;
        }
        return null;
    }, [headerType]);

    return (
        <>
            <button
                aria-label={format(loc(columnHeaderAriaLabel), columnLabel)}
                type="button"
                className={classnames(
                    columnHeader,
                    isTypeRampEnabled && columnHeaderTypeRampColorUpdate,
                    isTypeRampEnabled &&
                        currentSortColumn === sortColumnForHeader &&
                        !isSortingDisabled &&
                        columnHeaderTypeRamp,
                    useMessageListTextStyle('Major'),
                    isRPDenselessEnabled && bleedthroughColumnHeader,
                    headerType === 0 && senderColumnButtonMarginClass,
                    columnHeaderContainer,
                    headerType === 2 && receivedColumnContainer,
                    shouldApplySortedContentColumnStyles && sortedColumnHeaderTitle
                )}
                data-min-width={getColumnMinWidth(headerType)}
                disabled={isSortingDisabled}
                onClick={onClick}
                onContextMenu={onContextMenu}
                style={buttonStyles}
                title={columnLabel}
            >
                {headerType === 4 || headerType === 5 ? sortedContentHeaderIcon : columnLabel}
                {icon}
            </button>

            {resizeHandleComponent}

            {isContextMenuVisible && (
                <FluentMenu
                    directionalHint={DirectionalHint.bottomLeftEdge}
                    items={getMenuItems(
                        getSortColumnByMailListColumnHeaderType(headerType, folderName ?? ''),
                        onSortByContextualMenu,
                        tableViewId,
                        resizeHandleScenario
                    )}
                    onDismiss={onDismiss}
                    target={anchorPoint}
                />
            )}
        </>
    );
},
'MailListColumnHeader');

const getSortColumnByMailListColumnHeaderType = (
    mailListColumnHeaderType: MailListColumnHeaderType,
    folderName: string
): SortColumn => {
    switch (mailListColumnHeaderType) {
        case 0:
            return folderName == 'sentitems' || folderName == 'drafts' ? 9 : 3;
        case 1:
            return 8;
        case 2:
            return folderName == 'scheduled' ? 15 : 1;
        case 3:
            return 14;
        case 4:
            return 5;
        case 5:
            return 16;
        case 6:
            return 2;
        case 7:
            return 17;
        case 8:
            return 7;
    }
};

const getMenuItems = (
    sortColumn: SortColumn,
    onSortByContextualMenu: (sortColumn: SortColumn, sortDirection: SortDirection) => void,
    tableViewId: string,
    resizeHandleScenario: ResizeHandleScenario | null
): IContextualMenuItem[] => {
    const menuItems: IContextualMenuItem[] = [];

    if (!getIsSearchTableShown()) {
        menuItems.push(
            {
                key: 'Ascending',
                name: loc(sortAscendingLabel),
                onClick: () => {
                    onSortByContextualMenu(sortColumn, 'Ascending');
                    lazySelectSort.importAndExecute(
                        sortColumn,
                        'Ascending',
                        tableViewId,
                        'ListViewColumnHeaderContextMenu'
                    );
                },
            },
            {
                key: 'Descending',
                name: loc(sortDescendingLabel),
                onClick: () => {
                    onSortByContextualMenu(sortColumn, 'Descending');
                    lazySelectSort.importAndExecute(
                        sortColumn,
                        'Descending',
                        tableViewId,
                        'ListViewColumnHeaderContextMenu'
                    );
                },
            },
            getDisplayDensityMenuItem()
        );
    }

    const isColumnResizable = resizeHandleScenario !== null;
    if (isColumnResizable) {
        menuItems.push({
            key: 'Resize',
            name: loc(resizeButtonLabel),
            onClick: () => {
                lazyUpdateScenarioInResizeMode.importAndExecute(resizeHandleScenario);
            },
        });
    }

    return menuItems;
};

const getColumnLabel = (folderId: string, headerType: MailListColumnHeaderType): string => {
    const folderName = folderId ? folderIdToName(folderId, true) : null;

    switch (headerType) {
        case 0:
            return folderName == 'sentitems' || folderName == 'drafts' || folderName == 'outbox'
                ? loc(toColumnLabel)
                : loc(fromColumnLabel);
        case 1:
            return loc(subjectColumnLabel);
        case 2: {
            if (folderName == 'sentitems') {
                return loc(sentColumnLabel);
            } else if (folderName == 'drafts') {
                return loc(modifiedColumnLabel);
            } else if (folderName == 'scheduled') {
                return loc(returnTimeColumnLabel);
            } else {
                return loc(receivedColumnLabel);
            }
        }
        case 3:
            return loc(categoryColumnLabel);
        case 4:
            return loc(importanceColumnLabel);
        case 5:
            return loc(priorityColumnLabel);
        case 6:
            return loc(flagStatusColumnLabel);
        case 7:
            return loc(flagDueDateColumnLabel);
        case 8:
            return loc(sizeColumnLabel);
        default:
            return '';
    }
};

export default MailListColumnHeaders;
